import * as Y from 'yjs';
import { updateYFragment } from '@tiptap/y-tiptap';
import type { JSONContent } from '@tiptap/core';
import { nexusSchema } from '@nexus/editor';
import type { Store } from '@nexus/store';
import { docToJSON, FRAGMENT, loadDoc, reindexPage } from './content';

/**
 * Documents currently open in an editor. Changes made elsewhere (task views,
 * quick capture) go through the live document, so the editor shows them at once
 * and its own save loop persists them.
 */
const liveDocs = new Map<string, Y.Doc>();

export function registerLiveDoc(pageId: string, doc: Y.Doc): () => void {
  liveDocs.set(pageId, doc);
  return () => {
    if (liveDocs.get(pageId) === doc) liveDocs.delete(pageId);
  };
}

/**
 * Content that arrived from another device: if the page is open, merge it into the
 * editor's document right away (origin 'load', so it is not saved again).
 */
export function applyToLiveDoc(pageId: string, update: Uint8Array): boolean {
  const live = liveDocs.get(pageId);
  if (!live) return false;
  Y.applyUpdate(live, update, 'load');
  return true;
}

/** Origin of Yjs transactions made by Nexus itself (not by typing). */
export const NEXUS_ORIGIN = 'nexus';

/**
 * Change a page's document as JSON. The change is applied as a minimal Yjs diff,
 * so a concurrently open editor keeps the cursor and merges cleanly.
 * `fn` returns null for "no change".
 */
export function mutatePageDoc(
  store: Store,
  pageId: string,
  fn: (doc: JSONContent) => JSONContent | null,
): Promise<JSONContent | null> {
  // One change at a time per page, so each builds on the previous one.
  const previous = mutateQueue.get(pageId) ?? Promise.resolve(null);
  const next = previous.catch(() => null).then(() => mutateNow(store, pageId, fn));
  mutateQueue.set(pageId, next);
  void next.finally(() => {
    if (mutateQueue.get(pageId) === next) mutateQueue.delete(pageId);
  });
  return next;
}

const mutateQueue = new Map<string, Promise<JSONContent | null>>();

async function mutateNow(
  store: Store,
  pageId: string,
  fn: (doc: JSONContent) => JSONContent | null,
): Promise<JSONContent | null> {
  const live = liveDocs.get(pageId);
  const doc = live ?? (await loadDoc(store, pageId));
  const next = fn(docToJSON(doc));
  if (!next) {
    if (!live) doc.destroy();
    return null;
  }
  const updates: Uint8Array[] = [];
  const collect = (u: Uint8Array) => updates.push(u);
  if (!live) doc.on('update', collect);
  Y.transact(
    doc,
    () =>
      updateYFragment(doc, doc.getXmlFragment(FRAGMENT), nexusSchema().nodeFromJSON(next), {
        mapping: new Map(),
        isOMark: new Map(),
      }),
    NEXUS_ORIGIN,
  );
  if (!live) {
    doc.off('update', collect);
    if (updates.length) {
      const merged = Y.mergeUpdates(updates);
      await store.appendContent(pageId, merged);
      // An editor may have opened the page meanwhile: give it this change too.
      const nowLive = liveDocs.get(pageId);
      if (nowLive) Y.applyUpdate(nowLive, merged, 'load');
    }
    doc.destroy();
  }
  await reindexPage(store, pageId, next);
  return next;
}
